
import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Plus, Save, Trash2 } from "lucide-react";
import { useLanguages } from '@/hooks/useLanguages';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export function LanguageSettings() {
  const { languages, loading, error } = useLanguages(false); // Show all languages for settings
  const [saving, setSaving] = useState(false);
  const [defaultLanguage, setDefaultLanguage] = useState('en');
  const [newLanguageCode, setNewLanguageCode] = useState('');

  // Initialize default language from app settings
  useEffect(() => {
    const fetchDefaultLanguage = async () => {
      try {
        const { data, error } = await supabase
          .from('app_settings')
          .select('default_language')
          .eq('id', 1)
          .single();
        
        if (error) {
          console.warn('[LanguageSettings] Could not fetch default language:', error);
          return;
        }
        
        if (data?.default_language) {
          setDefaultLanguage(data.default_language);
        }
      } catch (err) {
        console.warn('[LanguageSettings] Error fetching default language:', err);
      }
    };
    
    fetchDefaultLanguage();
  }, []);

  const handleToggleLanguage = async (langCode: string, enabled: boolean) => {
    // Don't allow disabling if it's the default language
    if (!enabled && langCode === defaultLanguage) {
      toast.error("Cannot disable the default language");
      return;
    }

    try {
      setSaving(true);
      const { error } = await supabase
        .from('languages')
        .update({ active: enabled })
        .eq('code', langCode);

      if (error) {
        console.error('[LanguageSettings] Error updating language:', error);
        throw error;
      }
      
      toast.success(`Language ${enabled ? 'enabled' : 'disabled'} successfully`);
      
      // Refresh the languages list
      window.location.reload();
    } catch (error) {
      console.error('Error updating language:', error);
      toast.error('Failed to update language status. Please check your permissions.');
    } finally {
      setSaving(false);
    }
  };

  const handleAddCustomLanguage = async () => {
    if (!newLanguageCode) return;
    
    const existingLang = languages.find(lang => lang.code === newLanguageCode);
    if (existingLang) {
      toast.error('Language already exists');
      return;
    }

    try {
      setSaving(true);
      const { error } = await supabase
        .from('languages')
        .insert({
          code: newLanguageCode,
          name: newLanguageCode.toUpperCase(),
          native_name: newLanguageCode.toUpperCase(),
          active: true
        });

      if (error) {
        console.error('[LanguageSettings] Error adding language:', error);
        throw error;
      }
      
      setNewLanguageCode('');
      toast.success('Custom language added successfully');
      
      // Refresh the languages list
      window.location.reload();
    } catch (error) {
      console.error('Error adding language:', error);
      toast.error('Failed to add custom language. Please check your permissions.');
    } finally {
      setSaving(false);
    }
  };

  const handleDefaultLanguageChange = async (newDefault: string) => {
    try {
      setSaving(true);
      
      // Ensure the new default language is active
      await supabase
        .from('languages')
        .update({ active: true })
        .eq('code', newDefault);
      
      // Update the default language in app settings
      const { error } = await supabase
        .from('app_settings')
        .update({ default_language: newDefault })
        .eq('id', 1);

      if (error) {
        console.error('[LanguageSettings] Error updating default language:', error);
        throw error;
      }
      
      setDefaultLanguage(newDefault);
      toast.success('Default language updated successfully');
    } catch (error) {
      console.error('Error updating default language:', error);
      toast.error('Failed to update default language. Please check your permissions.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div>Loading languages...</div>;
  
  if (error) {
    return (
      <div className="p-4 border border-red-200 rounded-lg bg-red-50">
        <h3 className="font-medium text-red-800">Error Loading Languages</h3>
        <p className="text-sm text-red-600 mt-1">
          Unable to load language settings. Please check your connection and permissions.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <Label htmlFor="default-language">Default Language</Label>
        <Select value={defaultLanguage} onValueChange={handleDefaultLanguageChange} disabled={saving}>
          <SelectTrigger id="default-language" className="mt-1">
            <SelectValue placeholder="Select default language" />
          </SelectTrigger>
           <SelectContent className="bg-white">
             {languages.map((lang) => (
               <SelectItem key={lang.code} value={lang.code}>
                 {lang.name} ({lang.native_name})
               </SelectItem>
             ))}
           </SelectContent>
        </Select>
      </div>

      <div>
        <Label className="text-base font-medium">Available Languages</Label>
        <p className="text-sm text-muted-foreground mb-4">
          Toggle languages on/off to control which are available in forms
        </p>
        
        <div className="space-y-3">
          {languages.map((lang) => {
            const isEnabled = lang.active;
            const isDefault = lang.code === defaultLanguage;
            
            return (
              <div key={lang.code} className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-center gap-3">
                  <Switch
                    checked={isEnabled}
                    onCheckedChange={(checked) => handleToggleLanguage(lang.code, checked)}
                    disabled={isDefault || saving}
                  />
                  <div>
                    <div className="font-medium">{lang.name}</div>
                    <div className="text-sm text-muted-foreground">{lang.native_name}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {isDefault && <Badge variant="secondary">Default</Badge>}
                  {isEnabled && !isDefault && <Badge variant="outline">Enabled</Badge>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <Label className="text-base font-medium">Add Custom Language</Label>
        <p className="text-sm text-muted-foreground mb-2">
          Add a language not in the predefined list (use ISO 639-1 codes)
        </p>
        <div className="flex items-center gap-2">
          <Input
            placeholder="Language code (e.g., 'ko', 'hi')"
            value={newLanguageCode}
            onChange={(e) => setNewLanguageCode(e.target.value.toLowerCase())}
            className="flex-1"
            disabled={saving}
          />
          <Button
            variant="outline"
            onClick={handleAddCustomLanguage}
            disabled={!newLanguageCode || languages.some(lang => lang.code === newLanguageCode) || saving}
          >
            <Plus className="h-4 w-4 mr-1" />
            Add
          </Button>
        </div>
      </div>
    </div>
  );
}
